import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Mic, MonitorUp, Paperclip, Square } from 'lucide-react';
import VoiceRecorder from '../VoiceRecorder';
import Ltr from '../ui/Ltr';
import { toast } from '../../store/toastStore';
import {
  DEFAULT_MAX_BYTES, SCREEN_MAX_MS, VOICE_MAX_MS, acceptFiles, browserMediaEnv, mediaCapabilities, pickScreenMime,
  recordingClock, recordingFileName, screenRecordingName, screenshotAvailable, screenshotName,
} from './mediaModel';

/**
 * P-19a (D-145 / D-146 Q-99) — the ONE attach-and-record control: a file or
 * screenshot, a voice recording (MediaRecorder) and a screen recording
 * (getDisplayMedia, desktop only). Every finished capture calls `onAdd([file])`.
 * Used by "שליחת עדכון ללקוח" after a timer on a ticket, and by the portal's
 * new request / reply / project page (P-19b; the portal's own copy was folded
 * into this component at MERGE-INT7 — the screen recording keeps P-19b's
 * codec pick, ~1 Mbit/s bitrate and running clock).
 */
export interface MediaCaptureProps {
  onAdd: (files: File[]) => void;
  /** How many more files may be added (0 = the buttons are disabled). */
  remaining: number;
  disabled?: boolean;
  /** The file picker's accept list. */
  accept?: string;
  /** Per-file size limit in bytes (default 10 MB, the ticket attachment route). */
  maxBytes?: number;
  /** A screen recording's file-name label (default: "screen-YYYYMMDD-HHMMSS"). */
  screenFileLabel?: string;
  /** A small line under the buttons (limits). */
  hint?: ReactNode;
  /** Prepended to `hint` on a phone / tablet. */
  mobileHint?: ReactNode;
  /** True while a voice / screen recording is open and not yet attached (FIX-B7): the host must not send meanwhile. */
  onPendingChange?: (pending: boolean) => void;
}

const DEFAULT_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,application/pdf,.docx,.xlsx,.zip,audio/*,video/mp4,video/webm';

export default function MediaCapture({
  onAdd, remaining, disabled = false, accept = DEFAULT_ACCEPT, maxBytes = DEFAULT_MAX_BYTES, screenFileLabel, hint, mobileHint, onPendingChange,
}: MediaCaptureProps) {
  const { t } = useTranslation();
  const caps = useMemo(() => mediaCapabilities(browserMediaEnv()), []);
  // P-59a (D-235 §4): one frame of the screen, shown for approval before it is attached.
  const canShot = useMemo(() => screenshotAvailable(browserMediaEnv()), []);
  const [shot, setShot] = useState<{ file: File; url: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [voice, setVoice] = useState(false);
  const [screenMs, setScreenMs] = useState<number | null>(null);
  const recRef = useRef<{ rec: MediaRecorder; stream: MediaStream; tick: number } | null>(null);
  const full = remaining <= 0;
  const off = disabled || full;
  const screenOn = screenMs !== null;
  const pending = voice || screenOn || shot !== null;
  useEffect(() => { onPendingChange?.(pending); }, [pending, onPendingChange]);
  // Unmounting with a recording open: nothing is pending any more.
  useEffect(() => () => { onPendingChange?.(false); }, [onPendingChange]);

  // Leaving with a recording running: stop it and release the screen.
  useEffect(() => () => {
    const r = recRef.current;
    if (r) { window.clearInterval(r.tick); r.stream.getTracks().forEach((tr) => tr.stop()); }
  }, []);

  const add = (files: File[]) => {
    const { accepted, tooLarge, overLimit } = acceptFiles(files, remaining, maxBytes);
    if (tooLarge.length) toast('error', t('media.tooLarge', { mb: Math.round(maxBytes / 1048576) }));
    if (overLimit.length) toast('error', t('media.tooMany'));
    if (accepted.length) onAdd(accepted);
  };

  useEffect(() => () => { if (shot) URL.revokeObjectURL(shot.url); }, [shot]);

  const takeScreenshot = async () => {
    let stream: MediaStream | null = null;
    try {
      const md = navigator.mediaDevices as MediaDevices & { getDisplayMedia: (c?: unknown) => Promise<MediaStream> };
      stream = await md.getDisplayMedia({ video: true, audio: false });
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.srcObject = stream;
      await video.play();
      // Let the first real frame arrive before reading it.
      await new Promise<void>((resolve) => {
        const v = video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => void };
        if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(() => resolve()); else window.setTimeout(resolve, 300);
      });
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob || blob.size === 0) { toast('error', t('media.shotError')); return; }
      const file = new File([blob], screenshotName(new Date()), { type: 'image/png' });
      setShot({ file, url: URL.createObjectURL(file) });
    } catch (err) {
      // The user cancelled the picker: nothing to say.
      if ((err as { name?: string })?.name !== 'NotAllowedError') toast('error', t('media.shotError'));
    } finally {
      stream?.getTracks().forEach((tr) => tr.stop());
    }
  };

  const stopScreen = () => {
    const r = recRef.current;
    if (r && r.rec.state !== 'inactive') r.rec.stop();
  };

  const startScreen = async () => {
    try {
      const md = navigator.mediaDevices as MediaDevices & { getDisplayMedia: (c?: unknown) => Promise<MediaStream> };
      const stream = await md.getDisplayMedia({ video: true, audio: true });
      const mime = pickScreenMime((m) => MediaRecorder.isTypeSupported(m));
      // ~1 Mbit/s: five minutes stay under the portal's 50 MB recording limit.
      const rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 1_000_000 });
      const chunks: BlobPart[] = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        if (recRef.current) window.clearInterval(recRef.current.tick);
        recRef.current = null;
        setScreenMs(null);
        const type = (rec.mimeType || mime || 'video/webm').split(';')[0];
        const blob = new Blob(chunks, { type });
        if (blob.size === 0) { toast('error', t('media.screenError')); return; }
        const now = new Date();
        const name = screenFileLabel ? recordingFileName(screenFileLabel, type, now) : screenRecordingName(now).replace(/\.webm$/, /mp4/i.test(type) ? '.mp4' : '.webm');
        add([new File([blob], name, { type })]);
      };
      // The browser's own "stop sharing" ends the recording too.
      stream.getVideoTracks()[0]?.addEventListener('ended', () => stopScreen());
      const startedAt = Date.now();
      const tick = window.setInterval(() => {
        const elapsed = Date.now() - startedAt;
        setScreenMs(elapsed);
        if (elapsed >= SCREEN_MAX_MS) stopScreen();
      }, 500);
      recRef.current = { rec, stream, tick };
      rec.start(1000);
      setScreenMs(0);
    } catch (err) {
      // The user cancelled the picker: nothing to say.
      setScreenMs(null);
      if ((err as { name?: string })?.name !== 'NotAllowedError') toast('error', t('media.screenError'));
    }
  };

  if (voice) {
    return (
      <VoiceRecorder
        fullWidth
        autoStart
        confirmBeforeSend
        maxDurationMs={VOICE_MAX_MS}
        onRecordingComplete={(file) => { setVoice(false); add([file]); }}
        onCancel={() => setVoice(false)}
      />
    );
  }

  const btn = 'btn btn-secondary btn-sm inline-flex items-center gap-1.5';
  const hintLine = hint ? <>{caps.mobile && mobileHint ? <>{mobileHint} · </> : null}{hint}</> : null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('media.group')}>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={(e) => { add(Array.from(e.target.files ?? [])); e.target.value = ''; }}
        />
        <button type="button" className={btn} disabled={off || screenOn} onClick={() => fileRef.current?.click()}>
          <Paperclip size={16} aria-hidden="true" />
          {t('media.file')}
        </button>
        {caps.voice && (
          <button type="button" className={btn} disabled={off || screenOn} onClick={() => setVoice(true)}>
            <Mic size={16} aria-hidden="true" />
            {t('media.voice')}
          </button>
        )}
        {caps.screen && (screenOn ? (
          <button type="button" className={`${btn} text-danger-700`} onClick={stopScreen}>
            <Square size={14} aria-hidden="true" />
            {t('media.stopScreen')}
            <Ltr className="tabular-nums">{recordingClock(screenMs ?? 0)}</Ltr>
          </button>
        ) : (
          <button type="button" className={btn} disabled={off} onClick={() => void startScreen()}>
            <MonitorUp size={16} aria-hidden="true" />
            {t('media.screen')}
          </button>
        ))}
        {canShot && (
          <button type="button" className={btn} disabled={off || screenOn || shot !== null} onClick={() => void takeScreenshot()}>
            <Camera size={16} aria-hidden="true" />
            {t('media.screenshot')}
          </button>
        )}
        {full && <span className="text-xs text-text-2">{t('media.full')}</span>}
      </div>
      {shot && (
        <div className="space-y-2 rounded-lg border border-surface-200 bg-surface-0 p-2" role="group" aria-label={t('media.shotAlt')}>
          <img src={shot.url} alt={t('media.shotAlt')} className="max-h-48 max-w-full rounded-md border border-surface-200 object-contain" />
          <p className="text-xs text-text-2">{t('media.shotReady')}</p>
          <div className="flex gap-2">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => { const f = shot.file; setShot(null); add([f]); }}>{t('media.shotAttach')}</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShot(null)}>{t('media.shotCancel')}</button>
          </div>
        </div>
      )}
      {screenOn && (
        <p role="status" className="text-xs text-danger-700">{t('media.recording', { time: recordingClock(screenMs ?? 0) })}</p>
      )}
      {hintLine && <p className="text-xs text-text-2">{hintLine}</p>}
    </div>
  );
}
